import { buildAdjacency } from './flareSmoothingHelpers';

const DIST_EPS = 1e-7;

/** Unassigned segment id must lose ties to real segment indices (0..n). */
function segmentTieRank(seg) {
  return seg < 0 ? 256 : seg;
}

/**
 * Binary min-heap of (vid, dist, seg) entries stored in parallel typed arrays.
 * Ordering matches the legacy tie-break: lower distance wins; on near-tie prefer the lower
 * segment rank, then the lower vertex id. Keeping entries in typed arrays avoids per-push object
 * allocation during the geodesic sweep.
 */
class GeodesicHeap {
  constructor(capacity) {
    this._vid = new Int32Array(capacity);
    this._dist = new Float64Array(capacity);
    this._seg = new Int32Array(capacity);
    this.size = 0;
  }

  _ensure(extra) {
    const need = this.size + extra;
    if (need <= this._vid.length) return;
    let cap = this._vid.length * 2;
    while (cap < need) cap *= 2;
    const vid = new Int32Array(cap); vid.set(this._vid); this._vid = vid;
    const dist = new Float64Array(cap); dist.set(this._dist); this._dist = dist;
    const seg = new Int32Array(cap); seg.set(this._seg); this._seg = seg;
  }

  // Returns true when entry a should sort before entry b.
  _less(da, sa, va, db, sb, vb) {
    if (da < db - DIST_EPS) return true;
    if (db < da - DIST_EPS) return false;
    const ra = segmentTieRank(sa);
    const rb = segmentTieRank(sb);
    if (ra !== rb) return ra < rb;
    return va < vb;
  }

  push(vid, dist, seg) {
    this._ensure(1);
    let i = this.size++;
    this._vid[i] = vid; this._dist[i] = dist; this._seg[i] = seg;
    // Sift up
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this._less(this._dist[i], this._seg[i], this._vid[i], this._dist[p], this._seg[p], this._vid[p])) {
        this._swap(i, p);
        i = p;
      } else break;
    }
  }

  pop() {
    const top = { vid: this._vid[0], dist: this._dist[0], seg: this._seg[0] };
    const last = --this.size;
    if (last > 0) {
      this._vid[0] = this._vid[last];
      this._dist[0] = this._dist[last];
      this._seg[0] = this._seg[last];
      // Sift down
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let best = i;
        if (l < last && this._less(this._dist[l], this._seg[l], this._vid[l], this._dist[best], this._seg[best], this._vid[best])) best = l;
        if (r < last && this._less(this._dist[r], this._seg[r], this._vid[r], this._dist[best], this._seg[best], this._vid[best])) best = r;
        if (best === i) break;
        this._swap(i, best);
        i = best;
      }
    }
    return top;
  }

  _swap(a, b) {
    const tv = this._vid[a]; this._vid[a] = this._vid[b]; this._vid[b] = tv;
    const td = this._dist[a]; this._dist[a] = this._dist[b]; this._dist[b] = td;
    const ts = this._seg[a]; this._seg[a] = this._seg[b]; this._seg[b] = ts;
  }
}

/**
 * Multi-source geodesic distance and segment (Voronoi on mesh graph).
 *
 * Computed with multi-source Dijkstra over the 1-ring adjacency graph (edge weight = Euclidean
 * edge length). Each boundary vertex is a seed carrying a fixed segmentId; every vertex settles
 * with the distance and segment of its nearest seed. This is exact shortest-path — the same
 * partition the old Jacobi/Gauss–Seidel relaxation converged to — but it touches each in-band
 * vertex once instead of sweeping every triangle for hundreds of passes, which is the fix for the
 * multi-second freeze on large meshes.
 *
 * @param {THREE.BufferGeometry} geometry - Mesh geometry
 * @param {Map<number, number>} boundarySegmentMap - boundary vertex index -> segment index
 * @param {number} [passes] - Unused; kept for backward-compatible signature.
 * @param {Object} [options]
 * @param {number} [options.maxDistance=Infinity] - Stop expanding past this distance; vertices
 *   beyond the flare influence band stay Infinity (downstream discards them anyway).
 * @returns {{ distances: Float32Array, segmentIds: Int32Array }}
 */
export function computeGeodesicDistanceAndSegment(geometry, boundarySegmentMap, passes = 10, options = {}) {
  const maxDistance = Number.isFinite(options.maxDistance) ? options.maxDistance : Infinity;

  const positions = geometry.attributes.position;
  const pos = positions.array;
  const vertexCount = positions.count;
  const distances = new Float32Array(vertexCount);
  const segmentIds = new Int32Array(vertexCount);

  distances.fill(Infinity);
  segmentIds.fill(-1);

  // Reuse (and cache) the 1-ring adjacency; the smoothing passes that run later reuse it too.
  let adjacency = geometry.userData.flareAdjacency;
  if (!adjacency) {
    adjacency = buildAdjacency(geometry);
    geometry.userData.flareAdjacency = adjacency;
  }

  const heap = new GeodesicHeap(Math.max(64, boundarySegmentMap.size * 2));

  for (const [bv, seg] of boundarySegmentMap) {
    if (bv >= 0 && bv < vertexCount) {
      distances[bv] = 0;
      segmentIds[bv] = seg;
      heap.push(bv, 0, seg);
    }
  }

  const edgeLen = (a, b) => {
    const ai = a * 3, bi = b * 3;
    const dx = pos[ai] - pos[bi];
    const dy = pos[ai + 1] - pos[bi + 1];
    const dz = pos[ai + 2] - pos[bi + 2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  };

  while (heap.size > 0) {
    const { vid, dist, seg } = heap.pop();
    // Lazy deletion: only the entry matching the vertex's currently-settled distance AND label is
    // live; everything else was superseded by a shorter path or a lower-rank relabel.
    if (dist > distances[vid] + DIST_EPS) continue;
    if (seg !== segmentIds[vid]) continue;

    const nbrs = adjacency[vid];
    if (!nbrs) continue;

    for (let k = 0; k < nbrs.length; k++) {
      const n = nbrs[k];
      const nd = dist + edgeLen(vid, n);
      if (nd > maxDistance) continue;

      const cur = distances[n];
      if (nd < cur - DIST_EPS) {
        distances[n] = nd;
        segmentIds[n] = seg;
        heap.push(n, nd, seg);
      } else if (nd <= cur + DIST_EPS && segmentTieRank(seg) < segmentTieRank(segmentIds[n])) {
        // Equal-distance tie: relabel to the lower-ranked segment for deterministic Voronoi seams.
        segmentIds[n] = seg;
        heap.push(n, cur, seg);
      }
    }
  }

  return { distances, segmentIds };
}

/**
 * Geodesic distance from boundary (all boundary seeds share segment 0 internally).
 * @param {THREE.BufferGeometry} geometry - Mesh geometry
 * @param {Array<number>} boundaryVertices - Array of boundary vertex indices
 * @param {number} passes - Relaxation iterations
 * @returns {Float32Array} Distance field array
 */
export function computeDistanceFromBoundary(geometry, boundaryVertices, passes = 10, options = {}) {
  const map = new Map();
  for (let i = 0; i < boundaryVertices.length; i++) {
    map.set(boundaryVertices[i], 0);
  }
  return computeGeodesicDistanceAndSegment(geometry, map, passes, options).distances;
}

/**
 * Get maximum falloff distance from segment parameters
 * @param {Array} falloffHeightValues - Falloff height per segment (mm)
 * @param {Array} falloffDeflectValues - Falloff deflect per segment (mm)
 * @param {number} mmToUnit - Conversion factor (default: 0.1)
 * @returns {number} Maximum falloff distance in scene units
 */
export function getMaxFalloffDistance(falloffHeightValues, falloffDeflectValues, mmToUnit = 0.1) {
  const maxFalloff = Math.max(
    ...falloffHeightValues,
    ...falloffDeflectValues
  ) * mmToUnit;

  return maxFalloff;
}
